import React, { useState, useEffect, useImperativeHandle } from 'react';
import { Icon, TextField, Select, MenuItem, FormControl, InputLabel, Checkbox, List } from '@mui/material';
import { get, set, size } from 'global/lib/lodash';
import {
  BookmarkDialog,
  BookmarkDialogTitle,
  BookmarkDialogContent,
  BookmarkContent,
  SearchRow,
  CloneBtn,
  TableContainer,
  TableHeader,
  TableContent,
  TableRow,
  Col,
  BookmarkClose,
} from './styles';
import { useScreen, Loader } from 'global/styles';
import useRetrieveData from 'global/hooks/useRetrieveData';
import useDebounce from 'global/hooks/useDebounce';
import { ImageList } from 'global/components/image';
import withInfiniteScroll from 'global/components/withInfiniteScroll';
import { _size, scrollTop } from 'global/utils';
import { Button } from 'global/components/Button';
import NavToTop from 'feature/nav/NavToTop';
import moment from 'moment';

const DropDown = (props) => {
  return (
    <FormControl variant="standard">
      <InputLabel>{props.label}</InputLabel>
      <Select label={props.label} onChange={props.onChange}>
        <MenuItem value={undefined}>None</MenuItem>
        {props.options.map((opt) => (
          <MenuItem value={opt.id}>{opt.name}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const SectTypes = [
  { id: 'Best Practices', name: 'Best Practices' },
  { id: 'Quality Issues', name: 'Quality Opportunities' },
];

const SearchBar = () => {
  const { isMobileXS } = useScreen();
  const [searchVal, setSearchVal] = useState('');
  const searchDeb = useDebounce(searchVal, 1000);

  const [searchParams, setSearchParams] = useState({ useEffect: true, build: ['reportSections'], params: { page: { number: 1, size: 25 }, filter: { template: 1 }, include: 'superset,images,user' } });

  const { data, loading, meta, update } = useRetrieveData({ url: '/report-sections' }, searchParams);
  const supersets = useRetrieveData(
    { url: '/supersets' },
    {
      build: ['supersets'],
      useCache: { reload: 20 },
    }
  );

  const setParams = (selector, value) => {
    const sp = { ...searchParams };
    set(sp, 'params.page.number', 1);
    set(sp, selector, value);
    setSearchParams(sp);
  };

  const updatePage = (page) => {
    const sp = { ...searchParams };
    set(sp, 'params.page.number', page);
    setSearchParams(sp);
  };

  useEffect(() => {
    setParams('params.filter.search', searchDeb || undefined);
  }, [searchDeb]);

  return {
    data,
    meta,
    loading,
    updatePage,
    searchParams,
    update,
    Component: (
      <SearchRow>
        <TextField variant='standard' label={'Title or Author'} value={searchVal} onChange={(e) => setSearchVal(e.target.value)} />
        <DropDown label="System" options={supersets.data} onChange={(e) => setParams('params.filter.superset_id', e.target.value)} />
        <DropDown label="Highlight Type" options={SectTypes} onChange={(e) => setParams('params.filter.section_type', e.target.value)} />
      </SearchRow>
    ),
  };
};

const COLS = [
  { title: '', sel: 'check' },
  { title: 'Title', sel: 'title' },
  { title: 'System', sel: 'superset.name' },
  { title: 'Section Type', sel: 'sectionType' },
  { title: 'Author/Date', sel: (p) => ( `${get(p,'user.name') || ''} ${moment(get(p,'createdAt')).format('L')}` ) },
  { title: 'Text', sel: 'body', hide: (p) => p.isMobile },
  { title: 'Best Practice Photos', sel: 'images' },
];

const COLRow = (props) => {
  const { clickRow, chooseSelected, selected, item } = props;

  const { isMobileXS } = useScreen();

  return (
    <TableRow className="COL" isMobile={isMobileXS} onClick={(e) => clickRow(item, e)}>
      {COLS.map((col, _i) => {
        if (_i === 0) {
          return (
            <Col className="COL checkbox" colIndex={_i} isMobile={isMobileXS}>
              <Checkbox checked={!!get(selected, item.id)} onClick={(e) => chooseSelected(item, e)} size="small" />
            </Col>
          );
        }

        const content = (() => {
          if (col.sel === 'images') {
            return <ImageList images={get(item, 'images', [])} />;
          } else if (col.sel === 'body') {
            return <div className="COL" dangerouslySetInnerHTML={{ __html: get(item, col.sel, '') }} />;
          } else if (typeof col.sel === 'function') {
            return col.sel(item);
          } else {
            return get(item, col.sel, '');
          }
        })();

        const hide = col.hide ? col.hide({ isMobile: isMobileXS }) : false;

        return hide ? null : (
          <Col key={_i} className="COL" colIndex={_i} isMobile={isMobileXS} isText>
            {content}
          </Col>
        );
      })}
    </TableRow>
  );
};

// eslint-disable-next-line import/no-anonymous-default-export
export default React.forwardRef((props, ref) => {
  const { isMobileXS } = useScreen();
  const [selected, setSelected] = useState({});
  const [cloning, setCloning] = useState(false);

  const { data, loading, Component, updatePage, searchParams, meta, update } = SearchBar();

  useEffect(() => {
    if (props.open) {
      update();
    } else {
      setSelected({});
    }
  }, [props.open]);

  const clickRow = (row, e) => {
    //the ImageList images are inside of the clickable div, so we are doing this check to make sure we are actually "clicking" the row
    const checks = [get(e, 'target'), get(e, 'target.offsetParent')];
    if (size(checks.filter((c) => get(c, 'className', '').indexOf('COL') > -1))) {
      get(props, 'onClick', () => 0)(row);
    }
  };

  const stopProp = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const chooseSelected = (item, e) => {
    stopProp(e);
    const sel = { ...selected };
    if (get(sel, item.id)) {
      delete sel[item.id];
    } else {
      set(sel, item.id, item);
    }
    setSelected(sel);
  };

  const cloneAll = async () => {
    try {
      if (cloning) {
        return;
      }
      setCloning(true);
      await Promise.all(Object.keys(selected).map((key) => props.cloneItem(selected[key])));
      setCloning(false);
      props.onClose();
    } catch (err) {
      alert(`Error cloning items: ${err.message}`);
    }
  };

  const toggleAll = (e) => {
    if (!e.target.checked) {
      setSelected({});
    } else {
      const sel = {};
      data.map((it) => (sel[it.id] = true));
      setSelected(sel);
    }
  };

  useImperativeHandle(ref, () => ({
    updateAll: () => update({ force: true }),
  }), []);

  return (
    <BookmarkDialog isMobile={isMobileXS} keepMounted={props.keepMounted} fullScreen={isMobileXS} open={props.open} onClose={props.onClose} fullWidth={true} maxWidth="lg" id={'TemplateModal'}>
      <BookmarkDialogTitle>{'Add from Template'}</BookmarkDialogTitle>

      <BookmarkDialogContent>
        <BookmarkContent>
          {Component}

          {!!_size(selected) && (
            <CloneBtn>
              <Button startIcon={cloning && <Loader size={18} />} onClick={cloneAll}>
                Clone Highlight
              </Button>
            </CloneBtn>
          )}

          <TableContainer>
            {!isMobileXS && (
              <TableHeader as={TableRow} isMobile={isMobileXS}>
                {COLS.map((col, i) => (
                  <Col key={i} colIndex={i} isMobile={isMobileXS} style={{marginLeft: i === 1 ? 0 : undefined}}>
                    {i === 0 ? <Checkbox onClick={toggleAll} checked={!!(!loading && !!size(data) && Object.keys(selected).length === size(data))} size={'small'} /> : col.title}
                  </Col>
                ))}
              </TableHeader>
            )}
            <TableContent>
              <InfiniteList
                data={data}
                loading={loading}
                selected={selected}
                chooseSelected={chooseSelected}
                clickRow={clickRow}
                handlePageChange={updatePage}
                currentPage={get(searchParams, 'params.page.number', 1)}
                lastPage={get(meta, 'page.last-page')}
              />
            </TableContent>
            <NavToTop onClick={() => scrollTop('#TemplateModal .MuiDialogContent-root')} />
          </TableContainer>
        </BookmarkContent>
      </BookmarkDialogContent>

      <BookmarkClose onClick={props.onClose} size="large">
        <Icon>cancel</Icon>
      </BookmarkClose>
    </BookmarkDialog>
  );
});

const InfiniteList = withInfiniteScroll(COLRow, List);
